import { useState } from "react";
import { router } from "@inertiajs/react";
import type { PagyMetadata } from "@/components/pagination/PaginationControls";
import PaginationControls from "@/components/pagination/PaginationControls";
import PageHeader from "@/components/PageHeader";
import InertiaLink from "@/components/buttons/InertiaLink";
import Button from "@/components/buttons/Button";
import Tag from "@/components/Tag";
import DataTable, { DataTableColumn } from "@/components/DataTable";
import TableSearch from "@/components/inputs/TableSearch";
import TableFilter from "@/components/inputs/TableFilter";
import { SelectOption } from "@/components/inputs/Select";
import { PageWithLayout, wideLayout } from "@/components/Layout";

interface Writer {
  name: string;
  ipi_cae_number: string | null;
  pro_affiliation: string | null;
  split_percentage: number;
  mine: boolean;
}

interface SplitTrack {
  id: number;
  title: string;
  isrc_code: string | null;
  artist_name: string;
  my_split_percentage: number | null;
  my_track_writer_id: number | null;
  my_registered_at: string | null;
  writers: Writer[];
}

interface IndexProps {
  tracks: SplitTrack[];
  contributor_linked: boolean;
  title_q: string | null;
  status: string;
  pagy: PagyMetadata;
}

const searchReloadProps = ["tracks", "title_q", "status", "pagy"];

const statusOptions: SelectOption[] = [
  { value: "all", label: "All" },
  { value: "registered", label: "Registered" },
  { value: "unregistered", label: "Unregistered" },
];

const EMPTY_MESSAGE = "No tracks found.";

const formatSplit = (split: number | null) =>
  split === null ? "—" : `${split}%`;

const formatRegisteredDate = (registeredAt: string) =>
  new Date(registeredAt).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

const baseColumns: DataTableColumn<SplitTrack>[] = [
  {
    label: "Track",
    minWidth: "min-w-48",
    cellClassName: "font-medium",
    render: (track) => track.title,
  },
  {
    label: "Artist",
    minWidth: "min-w-32",
    cellClassName: "font-light",
    render: (track) => track.artist_name,
  },
  {
    label: "ISRC",
    minWidth: "min-w-36",
    cellClassName: "font-light",
    render: (track) => track.isrc_code ?? "—",
  },
  {
    label: "My split",
    minWidth: "min-w-28",
    cellClassName: "font-medium",
    render: (track) => formatSplit(track.my_split_percentage),
  },
];

const statusColumn: DataTableColumn<SplitTrack> = {
  label: "Status",
  minWidth: "min-w-36",
  clamp: false,
  render: (track) => (
    <div className="flex">
      {track.my_registered_at ? (
        <Tag label="Registered" variant="success" />
      ) : (
        <Tag label="Unregistered" />
      )}
    </div>
  ),
};

const buildColumns = (status: string): DataTableColumn<SplitTrack>[] =>
  status === "all" ? [...baseColumns, statusColumn] : baseColumns;

const WRITER_ROW_CLASSES =
  "text-cream-900 dark:text-cream-100 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3 lg:justify-start";

const WRITER_CARD_CLASSES =
  "max-lg:border-cream-300 dark:max-lg:border-cream-800 max-lg:bg-cream-100 dark:max-lg:bg-cream-950 max-lg:rounded-2xl max-lg:border max-lg:px-4 max-lg:py-4";

const writerIdentity = (writer: Writer, meta?: React.ReactNode) => (
  <>
    <div className="flex min-w-0 flex-col gap-1 max-lg:w-full lg:min-w-48">
      <div className="flex items-baseline justify-between gap-x-6">
        <span className="font-medium">{writer.name}</span>
        <span className="font-medium lg:hidden">
          {writer.split_percentage}%
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-cream-600 dark:text-cream-400 text-sm">
          IPI/CAE {writer.ipi_cae_number ?? "—"}
        </span>
        {meta}
      </div>
    </div>
    <span className="font-medium max-lg:hidden">
      {writer.split_percentage}%
    </span>
  </>
);

const writerBreakdown = (
  track: SplitTrack,
  onToggleRegistration: (track: SplitTrack) => void,
  pending: boolean,
) => {
  const myWriters = track.writers.filter((writer) => writer.mine);
  const otherWriters = track.writers.filter((writer) => !writer.mine);

  return (
    <div className="border-cream-300 dark:border-cream-800 lg:bg-cream-100 dark:lg:bg-cream-950 flex flex-col gap-4 rounded-2xl lg:border lg:p-6">
      <div className="flex flex-col gap-3">
        <p className="text-cream-600 dark:text-cream-400 text-sm font-medium">
          Writers
        </p>
        {myWriters.map((writer) => (
          <div
            key={writer.name}
            className={`${WRITER_ROW_CLASSES} ${WRITER_CARD_CLASSES}`}
          >
            {writerIdentity(
              writer,
              <span className="lg:hidden">
                {track.my_registered_at ? (
                  <Tag
                    label={`Reg ${formatRegisteredDate(track.my_registered_at)}`}
                    variant="success"
                    size="sm"
                  />
                ) : (
                  <Tag label="Unregistered" size="sm" />
                )}
              </span>,
            )}
            <div className="flex w-full flex-wrap items-baseline gap-3 lg:w-auto lg:gap-6">
              <span className="hidden lg:block">
                {track.my_registered_at ? (
                  <Tag
                    label={`Registered ${formatRegisteredDate(track.my_registered_at)}`}
                    variant="success"
                  />
                ) : (
                  <Tag label="Unregistered" />
                )}
              </span>
              {track.my_track_writer_id !== null && (
                <Button
                  text={
                    track.my_registered_at
                      ? "Mark as unregistered"
                      : "Mark as registered"
                  }
                  type="secondary"
                  htmlButtonType="button"
                  className="w-full max-lg:py-2.5 max-lg:text-sm lg:w-auto"
                  disabled={pending}
                  onClick={() => onToggleRegistration(track)}
                />
              )}
            </div>
          </div>
        ))}
        {otherWriters.length > 0 && (
          <div className={`flex flex-col gap-3 ${WRITER_CARD_CLASSES}`}>
            {otherWriters.map((writer) => (
              <div key={writer.name} className={WRITER_ROW_CLASSES}>
                {writerIdentity(writer)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Index: PageWithLayout<IndexProps> = ({
  tracks,
  contributor_linked,
  title_q,
  status,
  pagy,
}) => {
  const [searching, setSearching] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);

  const toggleRegistration = (track: SplitTrack) => {
    const trackWriterId = track.my_track_writer_id;
    if (trackWriterId === null) return;

    const url = `/splits/${trackWriterId}/registration${window.location.search}`;
    const options = {
      preserveScroll: true,
      preserveState: true,
      onStart: () => setPendingId(trackWriterId),
      onFinish: () => setPendingId(null),
    };

    if (track.my_registered_at) {
      router.delete(url, options);
    } else {
      router.put(url, {}, options);
    }
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-10">
      <PageHeader title="My writer splits" />
      {contributor_linked ? (
        <div className="flex w-full flex-col items-start justify-start gap-5">
          <div className="flex w-full flex-col gap-4 md:flex-row md:items-end">
            <div className="w-full md:max-w-sm">
              <TableSearch
                placeholder="Search by title"
                queryParam="title_q"
                initialValue={title_q ?? ""}
                only={searchReloadProps}
                onLoadingChange={setSearching}
              />
            </div>
            <div className="w-full md:max-w-52">
              <TableFilter
                queryParam="status"
                value={status}
                options={statusOptions}
                defaultValue="all"
                only={searchReloadProps}
                onLoadingChange={setSearching}
              />
            </div>
          </div>
          <DataTable
            columns={buildColumns(status)}
            rows={tracks}
            rowKey={(track) => track.id}
            expandedContent={(track) =>
              writerBreakdown(
                track,
                toggleRegistration,
                pendingId !== null && pendingId === track.my_track_writer_id,
              )
            }
            emptyMessage={EMPTY_MESSAGE}
            loading={searching}
          />
          <PaginationControls {...pagy} />
        </div>
      ) : (
        <div className="dark:bg-cream-950 flex w-full flex-col items-start gap-6 rounded-2xl bg-white p-6 shadow md:p-10">
          <p className="text-cream-600 dark:text-cream-400 text-sm">
            Your account isn&apos;t linked to a contributor record yet, so we
            can&apos;t tell which tracks you&apos;ve written on. Link your
            account to a contributor to see your splits.
          </p>
          <InertiaLink
            href="/contributors"
            type="primary"
            text="Go to contributors"
          />
        </div>
      )}
    </div>
  );
};

Index.layout = wideLayout;

export default Index;
